import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';

import numeral from 'numeral';

interface WarehouseResortFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    product: any;
    form: any;
    schema: any;

    meta: any;
}

const state: WarehouseResortFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    product: null,
    form: null,
    schema: null,

    meta: {},
};

const initState: WarehouseResortFormState = cloneDeep(state);

export const WAREHOUSES_RESORT_DATA = 'GET_WAREHOUSES_RESORT_DATA';
export const WAREHOUSES_RESORT_SCHEMA = 'GET_WAREHOUSES_RESORT_SCHEMA';

export const actions: ActionTree<WarehouseResortFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(WAREHOUSES_RESORT_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', result.getOperation(WAREHOUSES_RESORT_DATA).getContent());

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(WAREHOUSES_RESORT_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:warehouses_resort`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(WAREHOUSES_RESORT_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit, rootState }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        normalizedForm.manager_id = (rootState as any).ubic.actor.document.id

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('WAREHOUSES_DATA_SAVE')
                    .update(documentId, normalizedForm)
            } else {
                saveQuery = vm.$q('WAREHOUSES_DATA_SAVE')
                    .create('s:warehouses_resort', normalizedForm)
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('WAREHOUSES_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    setProduct({state}, product) {
        state.product = product
    }

};

export const mutations: MutationTree<WarehouseResortFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_WAREHOUSES_RESORT_DATA(state, {document}) {
        state.document = document;
    },

    SET_FORM_DATA(state, {document, schema}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: WarehouseResortFormState, {field, newValue}) {
        switch (field) {
            case 'quantity':
                state.form[field] = numeral(newValue.replace(',', '.')).value();
                // @ts-ignore
                state.form.weight = (numeral(state.form[field]).value() * numeral(state.product.weight).value()).toFixed(2)
                break;

            case 'weight':
                state.form[field] = numeral(newValue.replace(',', '.')).value();
                // @ts-ignore
                state.form.quantity = Math.ceil(numeral(state.form[field]).value() / numeral(state.product.weight).value())
                break;

            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: WarehouseResortFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_warehouse_resort_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
